import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Paper,
  Snackbar,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
} from "@mui/material";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import DeveloperBoardIcon from "@mui/icons-material/DeveloperBoard";
import InventoryIcon from "@mui/icons-material/Inventory";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import SpaIcon from "@mui/icons-material/Spa";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SidebarLayout from "../../shared/sections/sidebar/SidebarLayout";
import { useAuth } from "../../context/AuthContext";
import { apiFetch } from "../../api/apiClient";
import { useAppWorkspaceStatus } from "../../hooks/useAppWorkspaceStatus";
import {
  APP_WORKSPACE_MODEL_TYPE,
  APP_WORKSPACE_STATUS,
  APP_WORKSPACE_TOWERS,
  MECHANICAL_COMMODITY_ORDER,
  filterCommoditiesByModelTab,
  getAllWorkspaceItems,
  getDefaultTowerModelTab,
  getTowerModelTabs,
  getWorkspaceItemKey,
  getWorkspaceItemStatus,
  isPerlabParametricCommodity,
} from "../../utils/appWorkspaceConfig";

const workspaceCatalog = getAllWorkspaceItems();
const brandColor = "#ff6034";
const surfaceColor = "#242728";
const borderColor = "rgba(241, 242, 243, 0.08)";
const textPrimary = "#F1F2F3";
const textSecondary = "#798286";

const towerIconMap = {
  mechanical: <PrecisionManufacturingIcon sx={{ fontSize: 24 }} />,
  electronics: <DeveloperBoardIcon sx={{ fontSize: 24 }} />,
  packaging: <InventoryIcon sx={{ fontSize: 24 }} />,
  fnb: <RestaurantIcon sx={{ fontSize: 24 }} />,
  bpch: <SpaIcon sx={{ fontSize: 24 }} />,
};

const statusOptions = [
  {
    value: APP_WORKSPACE_STATUS.PUBLISHED,
    label: "Published",
    icon: <CheckCircleIcon sx={{ fontSize: 16 }} />,
  },
  {
    value: APP_WORKSPACE_STATUS.COMING_SOON,
    label: "Coming Soon",
    icon: <ScheduleIcon sx={{ fontSize: 16 }} />,
  },
];

const getNormalizedRole = (user) => {
  if (Array.isArray(user?.role) && user.role.length > 0) {
    return String(user.role[0]?.role_name || "")
      .trim()
      .toLowerCase();
  }

  return String(user?.role || "")
    .trim()
    .toLowerCase();
};

const AppWorkspacePage = () => {
  const { user } = useAuth();
  const { statusMap, loading, refreshStatuses } = useAppWorkspaceStatus();
  const [workspaceRows, setWorkspaceRows] = useState([]);
  const [savedRows, setSavedRows] = useState([]);
  const [activeTowerTab, setActiveTowerTab] = useState(0);
  const [activeModelTab, setActiveModelTab] = useState(APP_WORKSPACE_MODEL_TYPE.AI_POWERED);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const isSuperAdmin = getNormalizedRole(user) === "super admin";

  useEffect(() => {
    const mappedRows = workspaceCatalog.map((item) => {
      const statusKey = getWorkspaceItemKey(
        item.towerTitle,
        item.commodityName,
        item.modelType,
      );

      return {
        ...item,
        status: getWorkspaceItemStatus(
          statusMap,
          item.towerTitle,
          item.commodityName,
          item.modelType,
        ),
        statusConfigured: Boolean(statusMap?.[statusKey]),
      };
    });

    setWorkspaceRows(mappedRows);
    setSavedRows(mappedRows);
  }, [statusMap]);

  const workspaceTowers = useMemo(
    () =>
      APP_WORKSPACE_TOWERS.map((tower) => ({
        ...tower,
        icon: towerIconMap[tower.id],
        commodities: workspaceRows.filter((row) => row.towerId === tower.id),
      })).filter((tower) => tower.commodities.length > 0),
    [workspaceRows],
  );

  useEffect(() => {
    if (activeTowerTab >= workspaceTowers.length) {
      setActiveTowerTab(0);
    }
  }, [activeTowerTab, workspaceTowers.length]);

  const activeTower = workspaceTowers[activeTowerTab];
  const activeTowerModelTabs = useMemo(
    () => getTowerModelTabs(activeTower?.commodities || []),
    [activeTower?.commodities],
  );
  const activeTowerModelTabIds = activeTowerModelTabs.map((tab) => tab.id).join("|");

  useEffect(() => {
    if (!activeTower) return;
    setActiveModelTab(getDefaultTowerModelTab(activeTower.commodities));
  }, [activeTower?.id, activeTowerModelTabIds]);

  useEffect(() => {
    if (
      activeTowerModelTabs.length > 0 &&
      !activeTowerModelTabs.some((tab) => tab.id === activeModelTab)
    ) {
      setActiveModelTab(getDefaultTowerModelTab(activeTower?.commodities || []));
    }
  }, [activeModelTab, activeTower?.commodities, activeTowerModelTabs]);

  const displayedRows = useMemo(() => {
    const filteredRows = filterCommoditiesByModelTab(
      activeTower?.commodities || [],
      activeModelTab,
    );

    if (activeTower?.id !== "mechanical") {
      return filteredRows;
    }

    return [...filteredRows].sort(
      (a, b) =>
        MECHANICAL_COMMODITY_ORDER.indexOf(a.commodityName) -
        MECHANICAL_COMMODITY_ORDER.indexOf(b.commodityName),
    );
  }, [activeModelTab, activeTower?.commodities, activeTower?.id]);

  const workspaceSummary = useMemo(
    () =>
      workspaceRows.reduce(
        (summary, row) => {
          summary.total += 1;
          if (row.status === APP_WORKSPACE_STATUS.PUBLISHED) {
            summary.published += 1;
          } else {
            summary.comingSoon += 1;
          }
          if (!row.statusConfigured) {
            summary.newItems += 1;
          }
          return summary;
        },
        { total: 0, published: 0, comingSoon: 0, newItems: 0 },
      ),
    [workspaceRows],
  );

  const statusChanged =
    JSON.stringify(
      workspaceRows.map((row) => ({
        id: row.id,
        status: row.status,
      })),
    ) !==
    JSON.stringify(
      savedRows.map((row) => ({
        id: row.id,
        status: row.status,
      })),
    );
  const hasCatalogItemsMissingStatus = workspaceRows.some((row) => !row.statusConfigured);
  const hasUnsavedChanges = statusChanged || hasCatalogItemsMissingStatus;

  const handleStatusChange = (rowId, nextStatus) => {
    if (!nextStatus) return;
    setWorkspaceRows((currentRows) =>
      currentRows.map((row) => (row.id === rowId ? { ...row, status: nextStatus } : row)),
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await apiFetch("/api/app-workspace/statuses", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          statuses: workspaceRows.map((row) => ({
            tower: row.towerTitle,
            commodity: row.commodityName,
            model_type: row.modelType,
            status: row.status,
          })),
        }),
      });

      await refreshStatuses();
      setNotification({
        open: true,
        message: "App workspace saved successfully.",
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to save app workspace:", error);
      setNotification({
        open: true,
        message: error?.error || error?.message || "Failed to save app workspace.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <SidebarLayout>
        <Box sx={{ minHeight: "100vh", background: "#191A1B", py: 4 }}>
          <Container maxWidth="lg">
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              Super Admin access is required to manage the app workspace.
            </Alert>
          </Container>
        </Box>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <Box
        sx={{
          minHeight: "100vh",
          background: "#191A1B",
          py: 4,
          color: textPrimary,
        }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              flexWrap: "wrap",
              gap: 2,
              mb: 3,
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  color: textPrimary,
                  mb: 1,
                }}
              >
                App Workspace
              </Typography>
              <Typography sx={{ color: "#A0A8AB", maxWidth: 760 }}>
                Manage which commodity cards are published or shown as coming soon. This view uses
                the same tower and model tabs as the homepage catalog.
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || loading || !hasUnsavedChanges}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                backgroundColor: brandColor,
                "&:hover": {
                  backgroundColor: "#ff7b4a",
                },
                "&.Mui-disabled": {
                  backgroundColor: "rgba(255, 96, 52, 0.35)",
                  color: "rgba(255,255,255,0.6)",
                },
              }}
            >
              {saving ? "Saving..." : "Save Workspace"}
            </Button>
          </Box>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: "Total Apps", value: workspaceSummary.total },
              { label: "Published", value: workspaceSummary.published },
              { label: "Coming Soon", value: workspaceSummary.comingSoon },
              { label: "New In Catalog", value: workspaceSummary.newItems },
            ].map((item) => (
              <Grid item xs={6} md={3} key={item.label}>
                <Paper
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background: surfaceColor,
                    border: `1px solid ${borderColor}`,
                  }}
                >
                  <Typography sx={{ color: textSecondary, fontSize: "0.78rem", mb: 0.5 }}>
                    {item.label}
                  </Typography>
                  <Typography sx={{ color: textPrimary, fontWeight: 700, fontSize: "1.35rem" }}>
                    {item.value}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {workspaceTowers.length === 0 ? (
            <Paper
              sx={{
                background: surfaceColor,
                border: `1px solid ${borderColor}`,
                borderRadius: 3,
                p: 3,
                mb: 3,
              }}
            >
              <Typography sx={{ color: textSecondary }}>
                {loading ? "Loading app workspace..." : "No commodities are configured yet."}
              </Typography>
            </Paper>
          ) : (
          <Paper
            sx={{
              background: surfaceColor,
              border: `1px solid ${borderColor}`,
              borderRadius: 3,
              overflow: "hidden",
              mb: 3,
            }}
          >
            <Tabs
              value={Math.min(activeTowerTab, Math.max(workspaceTowers.length - 1, 0))}
              onChange={(_, nextValue) => setActiveTowerTab(nextValue)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                px: 2,
                borderBottom: `1px solid ${borderColor}`,
                minHeight: 58,
                "& .MuiTabs-indicator": {
                  backgroundColor: brandColor,
                  height: 3,
                },
                "& .MuiTab-root": {
                  color: textSecondary,
                  textTransform: "none",
                  minHeight: 58,
                  mr: 1,
                  "&.Mui-selected": {
                    color: textPrimary,
                  },
                },
              }}
            >
              {workspaceTowers.map((tower) => (
                <Tab
                  key={tower.id}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {tower.icon}
                      <Typography sx={{ fontWeight: 600 }}>{tower.title}</Typography>
                      <Chip
                        size="small"
                        label={tower.commodities.length}
                        sx={{
                          height: 20,
                          color: textPrimary,
                          backgroundColor: alpha("#FFFFFF", 0.08),
                        }}
                      />
                    </Box>
                  }
                />
              ))}
            </Tabs>

            {activeTowerModelTabs.length > 0 ? (
              <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${borderColor}` }}>
                <Tabs
                  value={activeModelTab}
                  onChange={(_, nextValue) => setActiveModelTab(nextValue)}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{
                    minHeight: 42,
                    "& .MuiTabs-indicator": {
                      backgroundColor: textPrimary,
                      height: 2,
                    },
                    "& .MuiTab-root": {
                      color: textSecondary,
                      textTransform: "none",
                      minHeight: 42,
                      minWidth: "unset",
                      px: 0,
                      mr: 4,
                      "&.Mui-selected": {
                        color: textPrimary,
                      },
                    },
                  }}
                >
                  {activeTowerModelTabs.map((tab) => (
                    <Tab
                      key={tab.id}
                      value={tab.id}
                      label={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          {tab.id === APP_WORKSPACE_MODEL_TYPE.AI_POWERED ? (
                            <AutoAwesomeIcon sx={{ fontSize: 16, color: "#ff7b4a" }} />
                          ) : (
                            <AccountTreeIcon sx={{ fontSize: 16, color: "#22d3ee" }} />
                          )}
                          <Typography sx={{ fontSize: "0.9rem" }}>{tab.label}</Typography>
                        </Box>
                      }
                    />
                  ))}
                </Tabs>
              </Box>
            ) : null}

            <Box sx={{ p: 2.5 }}>
              {displayedRows.length === 0 ? (
                <Typography sx={{ color: textSecondary }}>
                  No commodities available for this model.
                </Typography>
              ) : (
                <Grid container spacing={2.5}>
                  {displayedRows.map((row) => {
                    const published = row.status === APP_WORKSPACE_STATUS.PUBLISHED;
                    const showPPMIcon = isPerlabParametricCommodity(row);

                    return (
                      <Grid item xs={12} sm={6} lg={4} xl={3} key={row.id}>
                        <Card
                          sx={{
                            height: "100%",
                            borderRadius: 2,
                            border: `1px solid ${
                              published ? alpha("#2ecc71", 0.38) : alpha("#f5c04c", 0.4)
                            }`,
                            background: "#202324",
                            boxShadow: "none",
                            overflow: "hidden",
                          }}
                        >
                          <CardContent
                            sx={{
                              p: 1.5,
                              display: "flex",
                              flexDirection: "column",
                              gap: 1.25,
                              height: "100%",
                              "&:last-child": { pb: 1.5 },
                            }}
                          >
                            <Box sx={{ display: "flex", gap: 1.25 }}>
                              <Box
                                sx={{
                                  width: 86,
                                  height: 86,
                                  minWidth: 86,
                                  borderRadius: 1.5,
                                  background: alpha(brandColor, 0.15),
                                  overflow: "hidden",
                                }}
                              >
                                {typeof row.icon === "string" && row.icon.startsWith("/images") ? (
                                  <img
                                    src={row.icon}
                                    alt={row.commodityName}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                      display: "block",
                                    }}
                                  />
                                ) : (
                                  row.icon
                                )}
                              </Box>
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 1,
                                    mb: 0.5,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      color: textPrimary,
                                      fontWeight: 700,
                                      fontSize: "0.95rem",
                                      lineHeight: 1.25,
                                    }}
                                  >
                                    {row.commodityName}
                                  </Typography>
                                  {showPPMIcon ? (
                                    <AccountTreeIcon sx={{ fontSize: 16, color: "#22d3ee" }} />
                                  ) : (
                                    <AutoAwesomeIcon sx={{ fontSize: 16, color: "#ff7b4a" }} />
                                  )}
                                </Box>
                                <Typography
                                  sx={{
                                    color: textSecondary,
                                    fontSize: "0.76rem",
                                    lineHeight: 1.35,
                                  }}
                                >
                                  {row.description}
                                </Typography>
                              </Box>
                            </Box>

                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                              <Chip
                                size="small"
                                label={published ? "Published" : "Coming Soon"}
                                sx={{
                                  color: published ? "#2ecc71" : "#f5c04c",
                                  backgroundColor: published
                                    ? alpha("#2ecc71", 0.12)
                                    : alpha("#f5c04c", 0.12),
                                  fontWeight: 700,
                                }}
                              />
                              {!row.statusConfigured ? (
                                <Chip
                                  size="small"
                                  label="New in catalog"
                                  sx={{
                                    color: "#22d3ee",
                                    backgroundColor: alpha("#22d3ee", 0.12),
                                    fontWeight: 700,
                                  }}
                                />
                              ) : null}
                            </Box>

                            <ToggleButtonGroup
                              exclusive
                              fullWidth
                              size="small"
                              value={row.status}
                              onChange={(_, nextStatus) => handleStatusChange(row.id, nextStatus)}
                              sx={{
                                mt: "auto",
                                "& .MuiToggleButton-root": {
                                  minHeight: 34,
                                  color: textSecondary,
                                  borderColor: "rgba(241, 242, 243, 0.16)",
                                  textTransform: "none",
                                  fontSize: "0.75rem",
                                  gap: 0.5,
                                  "&.Mui-selected": {
                                    color: textPrimary,
                                    backgroundColor: alpha(brandColor, 0.18),
                                    borderColor: brandColor,
                                  },
                                  "&.Mui-selected:hover": {
                                    backgroundColor: alpha(brandColor, 0.24),
                                  },
                                },
                              }}
                            >
                              {statusOptions.map((option) => (
                                <ToggleButton key={option.value} value={option.value}>
                                  {option.icon}
                                  {option.label}
                                </ToggleButton>
                              ))}
                            </ToggleButtonGroup>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Box>
          </Paper>
          )}
        </Container>

        <Snackbar
          open={notification.open}
          autoHideDuration={4000}
          onClose={() => setNotification((current) => ({ ...current, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setNotification((current) => ({ ...current, open: false }))}
            severity={notification.severity}
            sx={{ width: "100%" }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Box>
    </SidebarLayout>
  );
};

export default AppWorkspacePage;
